import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import delve from 'dlv';
import { pick } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IPaymentCardType } from '@rbi-ctg/offers';
import { IStore } from '@rbi-ctg/store';
import { encryptOrbitalCard } from 'components/add-payment-method-modal/orbital-encrypt-card';
import {
  AdyenCCFieldFormNames,
  IAdyenErrors,
  IAdyenOnBrand,
  IAdyenState,
} from 'components/credit-card-form-inputs/adyen-credit-card-form-inputs/types';
import { CurrentSelectedMethodType } from 'components/payment-method/types';
import StoreConfirmationModal from 'components/store-confirmation-modal';
import { AdyenPaymentType, CartPaymentCardType } from 'generated/rbi-graphql';
import { useCommitOrder } from 'hooks/commit-order';
import { ICommitOrderInput } from 'hooks/commit-order/types';
import { usePlaceCateringOrder } from 'hooks/place-catering-order';
import { useAppflow } from 'hooks/use-appflow';
import useDialogModal from 'hooks/use-dialog-modal';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import useEffectOnce from 'hooks/use-effect-once';
import useErrorModal from 'hooks/use-error-modal';
import { useFeatureRestaurantMerchantId } from 'hooks/use-feature-restaurant-merchant-id';
import { useThreeDS } from 'hooks/use-threeDS';
import { UseThreeDS } from 'hooks/use-threeDS/types';
import { getNonce } from 'remote/api/first-data';
import { IPayment } from 'remote/queries/order';
import useApplePay from 'state/apple-pay/hooks/use-apple-pay';
import { useAuthContext } from 'state/auth';
import {
  TransactionType,
  getGooglePaymentsConfigs,
} from 'state/google-pay/get-google-payments-configs';
import useGooglePay from 'state/google-pay/hooks/use-google-pay';
import { LaunchDarklyFlag, useFlag, useLDContext } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  SupportedCardType,
  defaultPaymentFieldVariation,
  defaultSupportedCardTypes,
} from 'state/launchdarkly/variations';
import { useLogger } from 'state/logger';
import { useOffersContext } from 'state/offers';
import { OrderStatus, useOrderContext } from 'state/order';
import { DeliveryStatus } from 'state/order/types';
import { usePaymentContext } from 'state/payment';
import {
  CASH_ACCOUNT_IDENTIFIER,
  FREE_ORDER_ACCOUNT_IDENTIFIER,
  PAYPAL_PAYMENT_METHOD_PLACEHOLDER,
} from 'state/payment/constants';
import { ThreeDSDataPayment, ThreeDSType } from 'state/payment/hooks/types';
import usePrepaidMethodReload from 'state/payment/hooks/use-prepaid-method-reload';
import {
  CardType,
  CardTypes,
  IApplePayDetails,
  IGooglePayDetails,
  PaymentMethodBrand,
  PaymentState,
} from 'state/payment/types';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { useUIContext } from 'state/ui';
import { ISOs, ISOsToRegions, getCountryAndCurrencyCodes } from 'utils/form/constants';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { GraphQLError } from 'utils/network';
import { RuleType, findFirstRuleOfTypeThatApplies } from 'utils/offers';
import {
  IAdyenPaymentState,
  IFirstpayState,
  IFraudPreventionValues,
  IPayMarkState,
  IPaycometState,
  IVrPaymentState,
  checkIfValidTip,
  extractPaymentTypeForLD,
  getCCFormErrors,
  getFraudPreventionValues,
  initialErrorState,
  initialPaymentState,
  mapDeliveryToCCFormAddress,
  onCCFormChange,
  splitExpiry,
  testCardPaymentState,
} from 'utils/payment';
import { mapPaymentMethodCardTypes } from 'utils/payment/map-payment-method-card-type';
import { isApplePay, isGooglePay } from 'utils/payment/native-payment';
import { routes } from 'utils/routing';
import { getLocalizedServiceMode, isCatering } from 'utils/service-mode';
import { handleThreeDSPayment } from 'utils/threeDS';

import { useHandleCommitError } from '../../hooks/use-handle-commit-error';
import { getOrderFailureMessageKey } from '../get-error-dialog-options';

import { UseOrderPayment } from './types';

/**
 * Used to prevent duplication between one-step-checkout and
 * order-payment. This can be removed once TWO_STEP_CHECKOUT flag is cleaned up
 */
export const useOrderPayment = ({
  permissionToUpdate,
  serverOrder,
  enableOrderTimedFire,
}: UseOrderPayment) => {
  const payment = usePaymentContext();
  const { checkoutPaymentMethodId, setCheckoutPaymentMethodId, checkoutPaymentMethod } = payment;
  const auth = useAuthContext();
  const order = useOrderContext();
  const offersCtx = useOffersContext();

  const { formatCurrencyForLocale, confirmStoreOnCheckout, shouldConfirmStoreOnCheckout } =
    useUIContext();
  const logger = useLogger();
  const { serviceMode } = useServiceModeContext();
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;

  const isDigitalWalletMigrationEnabled = useFlag(LaunchDarklyFlag.ENABLE_DIGITAL_WALLET_MIGRATION);

  const rawSupportedCardTypes: SupportedCardType[] =
    useFlag(LaunchDarklyFlag.SUPPORTED_CARD_BRANDS_VARIATIONS) || defaultSupportedCardTypes;
  const supportedCardTypes = rawSupportedCardTypes
    .map(cardType => mapPaymentMethodCardTypes(cardType))
    .filter(Boolean) as CardType[];

  const enableSaveCard = !useFlag(LaunchDarklyFlag.HIDE_SAVE_CARD);

  const [ErrorDialog, openErrorDialog, dismissErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Order Commit Error',
  });

  const {
    cateringPickupDateTime,
    deliveryAddress,
    alertOrderDeliveryMinimum,
    alertOrderCateringMinimum,
    emptyCart,
  } = useOrderContext();
  const { selectedOffer } = useOffersContext();
  const { email, isRestaurantAvailable, openOrderingUnavailableDialog, store } = useStoreContext();
  const { formatMessage } = useIntl();
  const { updateCheckoutSelections } = useLDContext();
  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const isCurbside = serviceMode === ServiceMode.CURBSIDE;
  const isCateringOrder = isCatering(serviceMode);
  const isDeliveryQuoted =
    isDelivery && serverOrder?.delivery?.status === DeliveryStatus.QUOTE_SUCCESSFUL;

  const billingCountry = delve(auth, 'user.details.isoCountryCode') || ISOs.USA;
  const userDetailsName = auth.user?.details.name;
  const [paymentValues, setPaymentValues] = useState(
    initialPaymentState({ billingCountry, userDetailsName, enableSaveCard })
  );
  const [errors, setErrors] = useState(initialErrorState);
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  const [isAdyenIdealMethodSelected, setIsAdyenIdealMethodSelected] = useState(false);
  const [isAddGiftCardMethodSelected, setIsAddGiftCardMethodSelected] = useState(false);
  const [isCommittingOrder, setCommittingOrder] = useState(false);
  const [isHandlingOrderCommit, setIsHandlingOrderCommit] = useState(false);
  const [commitOrderAfterPricing, setCommitOrderAfterPricing] = useState(false);
  const [isPaycometPaypalMethodSelected, setIsPaycometPaypalMethodSelected] = useState(false);
  const [isPaycometSodexoMethodSelected, setIsPaycometSodexoMethodSelected] = useState('');
  const [isFreeOrder, setIsFreeOrder] = useState(false);
  const [isPaycometChequeGourmetMethodSelected, setIsPaycometChequeGourmetMethodSelected] =
    useState('');
  const autoFill = useFlag(LaunchDarklyFlag.AUTO_FILL_TEST_CARD);
  const enableDelivery = useFlag(LaunchDarklyFlag.ENABLE_DELIVERY);
  const enableQueryVrPaymentMerchantId = useFlag(
    LaunchDarklyFlag.ENABLE_QUERY_VR_PAYMENT_MERCHANT_ID
  );
  const { getRestaurantVrPaymentMerchantId } = useFeatureRestaurantMerchantId();

  const {
    threeDSActiveFlow,
    setThreeDSActiveFlow,
    threeDSTransactionId,
    setThreeDSTransactionId,
    threeDSIframeContent,
    setThreeDSIframeContent,
    threeDSAcsUrl,
    setThreeDSAcsUrl,
    threeDSChallengeRequest,
    setThreeDSChallengeRequest,
    threeDSChallengeTokenResponse,
    setThreeDSChallengeTokenResponse,
    isChallengeRequest,
    isThreeDSMethodFlow,
    cleanThreeDSFlow: orderCleanThreeDSFlow,
  }: UseThreeDS = useThreeDS();

  const cleanThreeDSFlow = useCallback(() => {
    orderCleanThreeDSFlow();
    fdAccessTokenFirstRequest.current = null;
    paymentNonceFirstRequest.current = null;
    accountIdentifierFirstRequest.current = null;
    threeDSSaleId.current = null;
  }, [orderCleanThreeDSFlow]);

  const fdAccessTokenFirstRequest = useRef<string | null>(null);
  const paymentNonceFirstRequest = useRef<string | null>(null);
  const accountIdentifierFirstRequest = useRef<string | null>(null);
  const threeDSSaleId = useRef<string | null>(null);

  const [StoreConfirmationDialog, openStoreConfirmationModal, , dismissStoreConfirmationDialog] =
    useDialogModal({
      showCancel: true,
      Component: StoreConfirmationModal,
      modalAppearanceEventMessage: 'Confirmation Modal: Store Location',
    });

  const {
    placeCateringOrder,
    loading: isPlacingCateringOrder,
    success: placeCateringOrderSuccess,
  } = usePlaceCateringOrder({
    logger,
    rbiOrderId: serverOrder && serverOrder.rbiOrderId,
  });

  const {
    commitOrder,
    commitResult: { called: commitOrderCalled, error: commitError, loading: isCommitOrderLoading },
    forceCommitOrder,
    success: commitOrderSuccess,
    failure: commitOrderFailure,
  } = useCommitOrder({
    logger,
    rbiOrderId: serverOrder && serverOrder.rbiOrderId,
  });

  // hold on to the original orderId
  const prevOrderId = useRef<string | null>(null);
  useEffect(() => {
    if (serverOrder && !prevOrderId.current) {
      prevOrderId.current = serverOrder.rbiOrderId;
    }
  }, [serverOrder]);

  const { handleCommitError } = useHandleCommitError({
    errorMsgId: getOrderFailureMessageKey(payment.checkoutPaymentMethodId, commitError),
    openErrorDialog,
    dismissErrorDialog,
    onCoolingPeriodConfirm: forceCommitOrder,
    commitError,
  });

  const isOrderStatusFailureHandledRef = useRef(false);
  useEffect(() => {
    if (!isOrderStatusFailureHandledRef.current && commitOrderFailure) {
      isOrderStatusFailureHandledRef.current = true;
      // present the generic error modal.
      handleCommitError(new GraphQLError([]));
    }
  }, [isOrderStatusFailureHandledRef, commitOrderFailure, handleCommitError]);

  const isPlacingOrder =
    payment.loading ||
    isPlacingCateringOrder ||
    commitOrderAfterPricing ||
    isCommittingOrder ||
    isCommitOrderLoading ||
    (commitOrderCalled && !commitOrderSuccess && !commitError);

  const enableImCloseDisplay = useFlag(LaunchDarklyFlag.ENABLE_IM_CLOSE_DISPLAY);
  const onlySendPostalCode = useFlag(LaunchDarklyFlag.SEND_POSTAL_CODE_ONLY_FOR_FIRST_DATA_PAYMENT);

  const shouldCommitOrder = useMemo(
    () =>
      serviceMode === ServiceMode.DELIVERY ||
      serviceMode === ServiceMode.DRIVE_THRU ||
      serviceMode === ServiceMode.TABLE_SERVICE ||
      ((serviceMode === ServiceMode.TAKEOUT || serviceMode === ServiceMode.EAT_IN) &&
        (enableOrderTimedFire || payment.isFreeOrderPayment)),
    [enableOrderTimedFire, serviceMode, payment.isFreeOrderPayment]
  );

  const nonSelectedPaymentMethods = payment.paymentMethods.filter(
    method =>
      method.accountIdentifier !== payment.checkoutPaymentMethodId ||
      method.fdAccountId !== payment.checkoutPaymentMethodId
  );
  const selectedPaymentMethod = payment.paymentMethods.find(
    method =>
      method.accountIdentifier === payment.checkoutPaymentMethodId ||
      method.fdAccountId === payment.checkoutPaymentMethodId
  );

  const paymentMethods = selectedPaymentMethod
    ? [selectedPaymentMethod, ...nonSelectedPaymentMethods]
    : nonSelectedPaymentMethods;
  const showPaymentMethodSelection = !isPlacingOrder;
  const navigate = useNavigate();

  const {
    reloadAddPaymentMethod,
    reloadAmount,
    reloadPaymentValues,
    reloadGiftCardAndCheckout,
    setReloadPaymentValues,
    showReloadPaymentMethods,
    needReloadAgain,
  } = usePrepaidMethodReload({
    commitOrder,
    handleCommitError,
    order,
    payment,
    paymentMethods,
    user: auth.user,
    serverOrder,
    selectedPaymentMethod,
    shouldCommitOrder: !!shouldCommitOrder,
    storeEmail: email,
  });

  const showPrepaidTransferBalance = !!selectedPaymentMethod?.prepaid;

  useEffect(() => {
    const ldPaymentType = extractPaymentTypeForLD(checkoutPaymentMethod);
    updateCheckoutSelections(ldPaymentType);
  }, [checkoutPaymentMethod, selectedPaymentMethod, updateCheckoutSelections]);

  /**
   * Persist payment values in payment context state
   */
  const persistPaymentValuesToState = useCallback(
    (data: {
      encryptedCN?: string;
      encryptedEM?: string;
      encryptedEY?: string;
      encryptedSC?: string;
      accountIdentifier?: string;
      fdAccessToken?: string;
      fdNonce?: string;
      creditType: string;
      fraudPreventionValues: IFraudPreventionValues;
      googlePayDetails?: IGooglePayDetails;
      applePayDetails?: IApplePayDetails;
      saveCard?: boolean;
      state: PaymentState;
    }) => {
      payment.storePaymentValues(data);
    },
    [payment]
  );

  const handlePaymentMethodSelected = (newFdAccountId?: string) => {
    if (!newFdAccountId) {
      payment.setCheckoutPaymentMethodId('');
      return;
    }

    setShowAddPaymentMethod(false);
    setIsAdyenIdealMethodSelected(false);
    setIsAddGiftCardMethodSelected(false);
    setIsPaycometPaypalMethodSelected(false);
    setIsPaycometSodexoMethodSelected('');
    setIsPaycometChequeGourmetMethodSelected('');
    payment.setCheckoutPaymentMethodId(newFdAccountId);
  };

  // when checkout payment method have been updated from payment context
  // the flags to show add new credit card or add gift card
  // have to be updated.
  useEffect(() => {
    if (checkoutPaymentMethodId) {
      const isAddNewCardSelected =
        checkoutPaymentMethodId === CurrentSelectedMethodType.ADD_NEW_CARD;
      if (isAddNewCardSelected) {
        setCheckoutPaymentMethodId('');
      }
      const isAdyenIdealSelected =
        checkoutPaymentMethodId === CurrentSelectedMethodType.PAY_VIA_IDEAL;

      setShowAddPaymentMethod(isAddNewCardSelected);
      setIsAdyenIdealMethodSelected(isAdyenIdealSelected);
      setIsAddGiftCardMethodSelected(false);
      setIsPaycometPaypalMethodSelected(false);
      setIsPaycometSodexoMethodSelected('');
      setIsPaycometChequeGourmetMethodSelected('');
    }
  }, [setCheckoutPaymentMethodId, checkoutPaymentMethodId]);

  // adds First Data test card to improve QA experience
  useEffectOnce(() => {
    if (autoFill) {
      setPaymentValues(testCardPaymentState({ isDelivery, enableSaveCard }));
    }
  });

  useEffect(() => {
    const testBillingAddress = pick(
      testCardPaymentState({
        isDelivery: paymentValues.billingAddressSameAsDelivery,
        paymentFieldVariations,
      }),
      ['billingStreetAddress', 'billingApt', 'billingCity', 'billingState', 'billingZip']
    );

    if (paymentValues.billingAddressSameAsDelivery) {
      setPaymentValues(prevState => ({
        ...prevState,
        ...mapDeliveryToCCFormAddress(deliveryAddress),
      }));
    } else {
      setPaymentValues(prevState => {
        return {
          ...prevState,
          ...(autoFill ? testBillingAddress : mapDeliveryToCCFormAddress({})),
        };
      });
    }
  }, [
    paymentValues.billingAddressSameAsDelivery,
    deliveryAddress,
    autoFill,
    paymentFieldVariations,
  ]);

  const navigateToOrderConfirmation = useCallback(
    () =>
      serverOrder &&
      navigate(`${routes.orderConfirmation}/${serverOrder.rbiOrderId}`, {
        state: { showToast: true },
      }),
    [navigate, serverOrder]
  );

  useEffectOnUpdates(() => {
    if (!serverOrder) {
      return;
    }

    if (commitOrderSuccess || placeCateringOrderSuccess) {
      emptyCart();

      hapticNotification({ type: HapticsNotificationType.SUCCESS });
      navigateToOrderConfirmation();
      return;
    }
    if (serverOrder.status === OrderStatus.PRICE_SUCCESSFUL && commitOrderAfterPricing) {
      // If the orderId hasn't changed, it has not finished pricing as we get a new orderId when its done
      if (prevOrderId.current === serverOrder?.rbiOrderId || (isDelivery && !isDeliveryQuoted)) {
        return;
      }
      handleOrderCommit();
    }
  }, [
    commitOrderSuccess,
    serverOrder,
    prevOrderId,
    isDelivery,
    isDeliveryQuoted,
    commitOrderAfterPricing,
  ]);

  const onChange = useCallback(
    (name: string, value: string) => {
      const { state, formErrors } = onCCFormChange(
        name,
        value,
        paymentValues,
        errors,
        formatMessage,
        supportedCardTypes
      );
      setErrors(prevState => ({ ...prevState, ...formErrors }));
      setPaymentValues(prevState => ({ ...prevState, ...state }));
    },
    [paymentValues, errors, formatMessage, supportedCardTypes]
  );

  const handleAdyenInputChange = useCallback((state: IAdyenState) => {
    setPaymentValues(prevState => {
      const {
        encryptedCardNumber = '',
        encryptedExpiryMonth = '',
        encryptedExpiryYear = '',
        encryptedSecurityCode = '',
      } = state.data.paymentMethod;
      return {
        ...prevState,
        cardNumber: encryptedCardNumber,
        cvv: encryptedSecurityCode,
        expiry: '',
        encryptedExpiryMonth,
        encryptedExpiryYear,
        browserInfo: state.data.browserInfo,
      };
    });
  }, []);

  const handleAdyenBrandDetected = useCallback((adyenOnBrand: IAdyenOnBrand) => {
    setPaymentValues(prevState => {
      return {
        ...prevState,
        cardType: mapPaymentMethodCardTypes(adyenOnBrand.brand) as string,
      };
    });
  }, []);

  const handleAdyenInputErrors = useCallback((adyenErrors: IAdyenErrors) => {
    setErrors(prevState => {
      const field = AdyenCCFieldFormNames[adyenErrors.fieldType];
      return {
        ...prevState,
        [field]: adyenErrors.errorI18n,
      };
    });
  }, []);

  const handleFirstpaySuccess = useCallback(
    (state: IFirstpayState) => {
      setPaymentValues(state);
      // Preserve PayMark state in payment context, so it can be access in pickup card
      payment.storePaymentValues({ ...payment.paymentValues, state });
    },
    [setPaymentValues, payment]
  );

  const handleVrPaymentSuccess = useCallback(
    (state: IVrPaymentState) => {
      setPaymentValues(state);

      // Preserve VR Payments state in payment context, so it can be access in pickup card
      payment.storePaymentValues({ ...payment.paymentValues, state });
    },
    [setPaymentValues, payment]
  );

  const handlePayMarkSuccess = useCallback(
    (state: IPayMarkState) => {
      setPaymentValues(state);
      // Preserve PayMark state in payment context, so it can be access in pickup card
      payment.storePaymentValues({ ...payment.paymentValues, state });
    },
    [setPaymentValues, payment]
  );

  const handlePaycometSuccess = useCallback(
    (state: IPaycometState) => {
      setPaymentValues(state);
      // Preserve Paycomet state in payment context, so it can be access in pickup card
      payment.storePaymentValues({ ...payment.paymentValues, state });
    },
    [setPaymentValues, payment]
  );

  const focusElementWithError = (name: string) => {
    // TODO: Remove queryselector and replace with ref's
    const element: HTMLElement | null = document.querySelector(`[name=${name}]`);
    if (element) {
      element.focus();
    }
  };

  const validateCreditCardForm = useCallback(() => {
    const { formErrors, hasErrors } = payment.isHostedPage
      ? { formErrors: {}, hasErrors: false }
      : getCCFormErrors(
          paymentValues,
          errors,
          formatMessage,
          paymentFieldVariations,
          billingCountry,
          payment.isAdyen
        );

    if (hasErrors) {
      const nameOfFirstError = [
        'nameOnCard',
        'cardNumber',
        'cvv',
        'expiry',
        'billingStreetAddress',
        'billingApt',
        'billingCity',
        'billingState',
        'billingZip',
        'billingCountry',
      ].find(name => formErrors[name]);

      if (nameOfFirstError) {
        focusElementWithError(nameOfFirstError);
      }

      setErrors(prevState => ({ ...prevState, ...formErrors }));
      return false;
    }
    return true;
  }, [
    payment.isHostedPage,
    payment.isAdyen,
    paymentValues,
    errors,
    formatMessage,
    paymentFieldVariations,
    billingCountry,
  ]);

  const placeOrder = useCallback(async () => {
    if (!serverOrder) {
      return Promise.reject('Required parameter is not defined.');
    }

    const shouldValidateCreditCard =
      !reloadAddPaymentMethod || (reloadAddPaymentMethod && showReloadPaymentMethods);
    if (shouldValidateCreditCard) {
      const ccFormIsValid = validateCreditCardForm();
      if (!ccFormIsValid) {
        return;
      }
    }

    const isRestaurantAvailableForOrdering = await isRestaurantAvailable(store);

    if (!isRestaurantAvailableForOrdering) {
      return openOrderingUnavailableDialog();
    }

    if (!showAddPaymentMethod && selectedPaymentMethod?.prepaid) {
      return reloadGiftCardAndCheckout();
    }

    const reshapedPaymentValues = payment.transformPaymentValues({
      paymentValues,
      paymentFieldVariations,
    });

    const fraudPreventionValues = getFraudPreventionValues(paymentValues);

    let paymentNonce;
    let fdAccessToken;
    let accountIdentifier: string | undefined;

    if (payment.isFirstData) {
      if (fdAccessTokenFirstRequest.current && paymentNonceFirstRequest.current) {
        fdAccessToken = fdAccessTokenFirstRequest.current;
        paymentNonce = paymentNonceFirstRequest.current;
      } else {
        try {
          const response = await payment.getEncryptionDetails();

          fdAccessToken = fdAccessTokenFirstRequest.current = response.fdAccessToken;
          const nonceResponse = await getNonce(
            reshapedPaymentValues,
            response.fdPublicKey,
            response.fdApiKey,
            fdAccessToken,
            response.fdCustomerId,
            auth.user?.details.isoCountryCode as ISOs | undefined,
            onlySendPostalCode,
            response.algorithm
          );
          const nonce = await nonceResponse.json();
          // TODO: Share paymentNonce for when we save a users payment/card below? [payment.addPaymentMethod]
          paymentNonce = paymentNonceFirstRequest.current = nonce.token.tokenId;
        } catch (error) {
          openErrorDialog({
            message: formatMessage({ id: 'paymentAddingError' }),
            modalAppearanceEventMessage: 'Error: Fetching Encryption Details Failure',
            error,
          });

          return;
        }
      }
    }

    // First-Data only since Adyen adds the payment method as part of the commitOrder
    if (auth.user && paymentValues.saveCard && payment.isFirstData) {
      if (accountIdentifierFirstRequest.current) {
        accountIdentifier = accountIdentifierFirstRequest.current;
      } else {
        try {
          accountIdentifier = accountIdentifierFirstRequest.current =
            await payment.addPaymentMethod(reshapedPaymentValues);
        } catch (error) {
          logger.error({ error, message: 'Error adding payment method during checkout' });
          return;
        }
      }
    }

    if (shouldCommitOrder) {
      let commitInput: ICommitOrderInput;
      // Only for firstdata - 3DS
      let fdThreeDSInput;

      if (payment.isAdyen) {
        commitInput = {
          creditType: paymentValues.cardType,
          order,
          payment: {
            adyenInput: {
              encryptedPayload: {
                encryptedCN: paymentValues.cardNumber,
                encryptedEM: (paymentValues as IAdyenPaymentState).encryptedExpiryMonth,
                encryptedEY: (paymentValues as IAdyenPaymentState).encryptedExpiryYear,
                encryptedSC: paymentValues.cvv ?? '',
              },
              paymentType: AdyenPaymentType.TOKENIZED_CREDIT_CARD,
              savePaymentMethod: paymentValues.saveCard,
              returnUrl: window.location.href,
              browserInfo: (paymentValues as IAdyenPaymentState).browserInfo,
            },
            billingAddress: fraudPreventionValues.billingAddress,
            fullName: fraudPreventionValues.fullName,
          },
          storeEmail: email,
        };
      } else if (payment.isFirstpay) {
        const firstpayPaymentValues = paymentValues as IFirstpayState;
        commitInput = {
          creditType: firstpayPaymentValues.cardType,
          order,
          payment: {
            fullName: firstpayPaymentValues.nameOnCard || '',
            ccMetadata: {
              ccBin: firstpayPaymentValues.card.bin,
              ccExpiryMonth: firstpayPaymentValues.card.expiryMonth,
              ccExpiryYear: firstpayPaymentValues.card.expiryYear,
              ccLast4: firstpayPaymentValues.card.last4,
            },
            firstPayInput: {
              merchantAccount: firstpayPaymentValues.merchantAccount,
              pspReference: firstpayPaymentValues.pspReference,
              storePaymentMethod: firstpayPaymentValues.saveCard,
              storedPaymentMethodId: firstpayPaymentValues.cardNumber || undefined,
            },
          },
          skipCoolingPeriod: true,
        };
      } else if (payment.isPayMark) {
        const payMarkPaymentValues = paymentValues as IPayMarkState;
        commitInput = {
          creditType: payMarkPaymentValues.cardType,
          order,
          payment: {
            fullName: payMarkPaymentValues.nameOnCard || '',
            ccMetadata: {
              ccBin: payMarkPaymentValues.card.bin,
              ccExpiryMonth: payMarkPaymentValues.card.expiryMonth,
              ccExpiryYear: payMarkPaymentValues.card.expiryYear,
              ccLast4: payMarkPaymentValues.card.last4,
            },
            payMarkInput: {
              merchantAccount: payMarkPaymentValues.merchantAccount,
              pspReference: payMarkPaymentValues.pspReference,
              storePaymentMethod: payMarkPaymentValues.saveCard,
              storedPaymentMethodId: payMarkPaymentValues.cardNumber || undefined,
            },
          },
          skipCoolingPeriod: true,
        };
      } else if (payment.isPaycomet) {
        const payCometValues = paymentValues as IPaycometState;
        if (payCometValues.isPaypal) {
          commitInput = {
            creditType: CartPaymentCardType.PAYPAL,
            order,
            payment: {
              fullName: payCometValues.nameOnCard || '',
              ccMetadata: undefined,
              paycometInput: {
                pspReference: payCometValues.pspReference || '',
                storePaymentMethod: payCometValues.saveCard || false,
                storedPaymentMethodId: payCometValues.cardNumber || undefined,
              },
            },
            skipCoolingPeriod: true,
          };
        } else {
          let paymentMethodBrandValue = undefined;
          if (isPaycometSodexoMethodSelected || isPaycometChequeGourmetMethodSelected) {
            paymentMethodBrandValue = isPaycometSodexoMethodSelected
              ? PaymentMethodBrand.SODEXO
              : PaymentMethodBrand.CHEQUE_GOURMET;
          }
          commitInput = {
            creditType: payCometValues.cardType,
            order,
            payment: {
              fullName: payCometValues.nameOnCard || '',
              ccMetadata: {
                ccBin: payCometValues?.card?.bin || '',
                ccExpiryMonth: payCometValues?.card?.expiryMonth || '',
                ccExpiryYear: payCometValues?.card?.expiryYear || '',
                ccLast4: payCometValues?.card?.last4 || '',
              },
              paymentMethodBrand: paymentMethodBrandValue,
              paycometInput: {
                pspReference: payCometValues.pspReference || '',
                storePaymentMethod: payCometValues.saveCard || false,
                storedPaymentMethodId: payCometValues.cardNumber || undefined,
              },
            },
            skipCoolingPeriod: true,
          };
        }
      } else if (payment.isVrPayment) {
        const vrPaymentValues = paymentValues as IVrPaymentState;
        if (vrPaymentValues.card) {
          commitInput = {
            creditType: vrPaymentValues.cardType,
            order,
            payment: {
              fullName: vrPaymentValues.nameOnCard || '',
              ccMetadata: {
                ccBin: vrPaymentValues.card.bin,
                ccExpiryMonth: vrPaymentValues.card.expiryMonth,
                ccExpiryYear: vrPaymentValues.card.expiryYear,
                ccLast4: vrPaymentValues.card.last4,
              },
              vrPaymentInput: {
                merchantAccount: vrPaymentValues.merchantAccount,
                pspReference: vrPaymentValues.pspReference,
                storePaymentMethod: vrPaymentValues.saveCard,
                storedPaymentMethodId: vrPaymentValues.cardNumber || undefined,
              },
            },
            skipCoolingPeriod: true,
          };
        } else if (vrPaymentValues.paypal) {
          commitInput = {
            creditType: CartPaymentCardType.PAYPAL,
            order,
            payment: {
              fullName: vrPaymentValues.nameOnCard || '',
              ccMetadata: undefined,
              vrPaymentInput: {
                merchantAccount: vrPaymentValues.merchantAccount,
                pspReference: vrPaymentValues.pspReference,
                storedPaymentMethodId: undefined,
                storePaymentMethod: true,
                billingAgreementId: vrPaymentValues.paypal.billingAgreementId,
              },
            },
            skipCoolingPeriod: true,
          };
        } else {
          throw new Error('Unknown VR payment values');
        }
      } else if (payment.isOrbital) {
        const {
          cryptCard,
          cryptCvv,
          integrityCheck: pieIntegrityCheck,
          pieFormat,
          subscriberId: pieSubscriberId,
          mode: pieMode,
          keyId: pieKeyID,
          phase: piePhaseId,
        } = await encryptOrbitalCard(
          reshapedPaymentValues.cardNumber,
          reshapedPaymentValues.securityCode!
        );

        const billingAddress = {
          ...fraudPreventionValues.billingAddress,
          country: ISOsToRegions[paymentValues.billingCountry!],
        };

        commitInput = {
          creditType: CartPaymentCardType.CREDIT,
          order,
          payment: {
            billingAddress,
            fullName: fraudPreventionValues.fullName,
            orbitalInput: {
              encryptedPayload: {
                encryptedCardNum: cryptCard,
                encryptedCvv: cryptCvv,
                expiryMonth: reshapedPaymentValues.expiryDate?.month!,
                expiryYear: reshapedPaymentValues.expiryDate?.year!,
                pieFormat,
                pieMode,
                piePhaseId: `${piePhaseId}`,
                pieIntegrityCheck,
                pieSubscriberId,
                pieKeyID,
                cardBrand: paymentValues.cardType.toLowerCase(),
                bin: reshapedPaymentValues.cardNumber.slice(0, 6),
              },
              savePaymentMethod: paymentValues.saveCard,
            },
          },
          skipCoolingPeriod: true,
        };
      } else {
        const { expiryMonth, expiryYear } = splitExpiry(fraudPreventionValues.expiry ?? '');

        if (
          (threeDSActiveFlow === ThreeDSType.METHOD ||
            threeDSActiveFlow === ThreeDSType.CHALLENGE) &&
          threeDSSaleId.current &&
          threeDSTransactionId
        ) {
          fdThreeDSInput = {
            threeDSDetails: {
              saleId: threeDSSaleId.current,
              threeDSOptions: {
                transactionId: threeDSTransactionId,
                challengeResponse: threeDSChallengeTokenResponse,
              },
            },
          };
        }
        commitInput = {
          creditType: paymentValues.cardType,
          order,
          payment: {
            billingAddress: fraudPreventionValues.billingAddress,
            ccMetadata: {
              ccBin: fraudPreventionValues.ccBin,
              ccExpiryMonth: expiryMonth,
              ccExpiryYear: expiryYear,
              ccLast4: fraudPreventionValues.ccLast4,
            },
            firstDataInput: {
              accountIdentifier: paymentValues.saveCard ? accountIdentifier : undefined,
              fdAccessToken: paymentValues.saveCard ? undefined : fdAccessToken,
              fdNonce: paymentValues.saveCard ? undefined : paymentNonce,
            },
            fullName: fraudPreventionValues.fullName,
          },
          skipCoolingPeriod: true,
          storeEmail: email,
          threeDSDetails: fdThreeDSInput?.threeDSDetails ?? undefined,
        };
      }

      return commitOrder(commitInput);
    }

    if (isCateringOrder) {
      return placeCateringOrder({
        cateringInfo: { pickupTime: cateringPickupDateTime },
        fdAccessToken,
        fdNonce: paymentNonce,
        creditType:
          paymentValues.cardType as any /** @TODO consolidate CardTypes and CartPaymentCardType */,
        fullName: fraudPreventionValues.fullName,
        billingAddress: fraudPreventionValues.billingAddress,
        ccBin: fraudPreventionValues.ccBin,
        ccLast4: fraudPreventionValues.ccLast4,
        ccExpiryYear: fraudPreventionValues.expiry,
        storeEmail: email,
        rbiOrderId: serverOrder.rbiOrderId,
      }).catch((error: GraphQLError) => {
        handleCommitError(error);
      });
    }

    persistPaymentValuesToState({
      encryptedCN: paymentValues.cardNumber,
      encryptedEM: (paymentValues as IAdyenPaymentState).encryptedExpiryMonth,
      encryptedEY: (paymentValues as IAdyenPaymentState).encryptedExpiryYear,
      encryptedSC: paymentValues.cvv ?? '',
      fdAccessToken,
      fdNonce: paymentNonce,
      creditType: paymentValues.cardType,
      fraudPreventionValues,
      saveCard: paymentValues.saveCard,
      state: paymentValues,
    });

    hapticNotification({ type: HapticsNotificationType.SUCCESS });
    return navigateToOrderConfirmation();
  }, [
    serverOrder,
    reloadAddPaymentMethod,
    showReloadPaymentMethods,
    isRestaurantAvailable,
    store,
    showAddPaymentMethod,
    selectedPaymentMethod,
    payment,
    paymentValues,
    paymentFieldVariations,
    auth.user,
    shouldCommitOrder,
    isCateringOrder,
    persistPaymentValuesToState,
    navigateToOrderConfirmation,
    validateCreditCardForm,
    openOrderingUnavailableDialog,
    reloadGiftCardAndCheckout,
    onlySendPostalCode,
    openErrorDialog,
    formatMessage,
    logger,
    commitOrder,
    order,
    email,
    isPaycometSodexoMethodSelected,
    isPaycometChequeGourmetMethodSelected,
    threeDSActiveFlow,
    threeDSTransactionId,
    threeDSChallengeTokenResponse,
    placeCateringOrder,
    cateringPickupDateTime,
    handleCommitError,
  ]);

  const { requestingPayment: isGooglePayLoading, requestGooglePayPayment } = useGooglePay({
    onError: () => {
      logger.warn('requestGooglePayPayment error');
    },
    onCompleted: (data: IGooglePayDetails) => {
      if (!data) {
        logger.warn('User left the Google Pay Flow');
        return;
      }
      processOrderWithAccount({ googlePayDetails: data });
    },
  });

  const handleApplePay = ({ error, data }: { error?: string; data?: IApplePayDetails }): void => {
    if (data) {
      processOrderWithAccount({ applePayDetails: data });
      return;
    }
    if (error) {
      logger.warn(error);
    }
  };

  // FD apple pay reload
  const { requestApplePayPayment: prepaidRequestApplePayPayment } = useApplePay({
    handleApplePay,
    order,
    prepaid: true,
    serverOrder,
  });

  const processOrderWithAccount = useCallback(
    async (data?: { applePayDetails?: IApplePayDetails; googlePayDetails?: IGooglePayDetails }) => {
      if (selectedPaymentMethod?.prepaid) {
        let reloadPaymentDetails = data?.applePayDetails ?? data?.googlePayDetails;
        if (!reloadPaymentDetails && payment.prepaidReloadPaymentMethodId && reloadAmount > 0) {
          const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
          if (payment.prepaidReloadPaymentMethodId === CardTypes.GOOGLE_PAY) {
            // Get the checkout gateway configs for google pay
            let merchantId = store.vrPaymentMerchantId || '';

            if (enableQueryVrPaymentMerchantId && !merchantId) {
              merchantId = await getRestaurantVrPaymentMerchantId(store as IStore);
            }

            const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
              transactionType: TransactionType.PREPAID,
              countryCode,
              overrideMerchantId: merchantId,
            });

            return requestGooglePayPayment({
              countryCode,
              currencyCode,
              total: reloadAmount,
              gatewayMerchantId,
              gateway,
            });
          } else if (payment.prepaidReloadPaymentMethodId === CardTypes.APPLE_PAY) {
            const applePayParams = {
              countryCode,
              currencyCode,
              total: reloadAmount,
            };

            return prepaidRequestApplePayPayment(applePayParams);
          }
        }

        const result = await reloadGiftCardAndCheckout(reloadPaymentDetails);
        // Do NOT return for service modes that should not commit
        // (e.g Curbside)
        if (shouldCommitOrder) {
          return result;
        }
      }

      let paymentAccount;
      if (payment.isFreeOrderPayment) {
        paymentAccount = {
          cash: true,
          fdAccountId: FREE_ORDER_ACCOUNT_IDENTIFIER,
          accountIdentifier: FREE_ORDER_ACCOUNT_IDENTIFIER,
          chaseProfileId: null,
          credit: null,
          prepaid: null,
          paypal: null,
          ideal: null,
          paypalIdentifier: null,
          sodexo: null,
          chequeGourmet: null,
        };
      } else {
        paymentAccount = payment.paymentMethods.find(method => {
          const accountId = method.accountIdentifier ?? method.fdAccountId ?? '';
          return accountId === payment.checkoutPaymentMethodId;
        });
      }
      if (!paymentAccount || !serverOrder) {
        return Promise.reject('Required parameters are not defined.');
      }

      const creditType = paymentAccount.credit
        ? CartPaymentCardType[paymentAccount.credit.cardType.toUpperCase()]
        : null;

      const fraudPreventionValues = getFraudPreventionValues(paymentValues);

      const accountIdentifier =
        paymentAccount.accountIdentifier ?? paymentAccount.fdAccountId ?? '';
      const commitInput = {
        creditType,
        rbiOrderId: serverOrder.rbiOrderId,
        storeEmail: email,
        order,
      };
      const fullName = fraudPreventionValues.fullName;

      const applePayDetails = data?.applePayDetails;
      const googlePayDetails = data?.googlePayDetails;

      // The reason this is here is because we want to avoid committing the order EXCEPT for in these situations...
      // In other cases, we want to navigate to a separate route where the user must confirm being close by, before committing... hence the sort of complicated logic here.
      // TODO - this whole section deserves a proper refactor due to how important it is to conversions as a whole.
      if (shouldCommitOrder) {
        if (payment.isFreeOrderPayment && accountIdentifier === FREE_ORDER_ACCOUNT_IDENTIFIER) {
          const paymentDetails: IPayment = {
            cashPayment: true,
            fullName,
            paymentMethodBrand: paymentAccount?.paymentMethodBrand ?? undefined,
          };

          return commitOrder({
            ...commitInput,
            creditType: CASH_ACCOUNT_IDENTIFIER,
            payment: paymentDetails,
          });
        }

        // Digital pay
        if (applePayDetails || googlePayDetails) {
          const digitalPayDetails = {
            applePayDetails,
            googlePayDetails,
            fullName,
          };

          if (payment.isAdyen) {
            return commitOrder({
              ...commitInput,
              payment: {
                adyenInput: {
                  paymentType: applePayDetails
                    ? AdyenPaymentType.APPLE_PAY
                    : AdyenPaymentType.GOOGLE_PAY,
                },
                ...digitalPayDetails,
              },
            });
          }

          if (payment.isVrPayment) {
            return commitOrder({
              ...commitInput,
              creditType: applePayDetails
                ? CartPaymentCardType.APPLE_PAY
                : CartPaymentCardType.GOOGLE_PAY,
              ...digitalPayDetails,
              payment: {
                fullName,
                vrPaymentInput: {
                  merchantAccount: '',
                  pspReference: '',
                  storePaymentMethod: false,
                },
              },
            });
          }

          return commitOrder({
            ...commitInput,
            payment: {
              ...digitalPayDetails,
            },
          });
        }

        const isPaymentWithVoucher =
          paymentAccount.paymentMethodBrand === PaymentMethodBrand.SODEXO_VOUCHER ||
          paymentAccount.paymentMethodBrand === PaymentMethodBrand.CHEQUE_GOURMET_VOUCHER;
        if (payment.checkoutPaymentMethodId === CASH_ACCOUNT_IDENTIFIER || isPaymentWithVoucher) {
          const paymentDetails: IPayment = {
            cashPayment: true,
            fullName,
            paymentMethodBrand: paymentAccount?.paymentMethodBrand ?? undefined,
          };

          if (payment.isVrPayment) {
            // The GraphQL endpoint for VR Payment expects this object even
            // for Cash orders, otherwise the order creation fails
            paymentDetails.vrPaymentInput = {
              merchantAccount: '',
              pspReference: '',
              storePaymentMethod: true,
            };
          }

          return commitOrder({
            ...commitInput,
            creditType: CASH_ACCOUNT_IDENTIFIER,
            payment: paymentDetails,
          });
        }

        // Normal Pay
        if (payment.isAdyen) {
          return commitOrder({
            ...commitInput,
            payment: {
              adyenInput: {
                accountIdentifier,
                returnUrl: window.location.href,
                browserInfo: (paymentValues as IAdyenPaymentState).browserInfo,
              },
              fullName,
              ccMetadata: {
                ccLast4: paymentAccount.credit?.alias ?? '',
              },
            },
          });
        }

        if (payment.isVrPayment) {
          const vrPaymentValues = paymentValues as IVrPaymentState;
          if (vrPaymentValues.card) {
            return commitOrder({
              ...commitInput,
              payment: {
                fullName: vrPaymentValues.nameOnCard || '',
                ccMetadata: {
                  ccBin: vrPaymentValues.card.bin,
                  ccExpiryMonth: vrPaymentValues.card.expiryMonth,
                  ccExpiryYear: vrPaymentValues.card.expiryYear,
                  ccLast4: vrPaymentValues.card.last4,
                },
                vrPaymentInput: {
                  merchantAccount: vrPaymentValues.merchantAccount,
                  pspReference: vrPaymentValues.pspReference,
                  storePaymentMethod: vrPaymentValues.saveCard,
                  storedPaymentMethodId: vrPaymentValues.cardNumber || undefined,
                },
              },
              skipCoolingPeriod: true,
            });
          } else if (vrPaymentValues.paypal) {
            return commitOrder({
              ...commitInput,
              creditType: CartPaymentCardType.PAYPAL,
              payment: {
                fullName: '',
                ccMetadata: undefined,
                vrPaymentInput: {
                  merchantAccount: '',
                  pspReference: '', // not available in "registered" flow
                  storePaymentMethod: false,
                  storedPaymentMethodId: accountIdentifier,
                  deviceToken: vrPaymentValues.paypal.deviceToken,
                },
              },
              skipCoolingPeriod: true,
            });
          }
        }

        if (payment.isPayMark) {
          return commitOrder({
            ...commitInput,
            payment: {
              fullName: '',
              payMarkInput: {
                storedPaymentMethodId: accountIdentifier,
              },
              ccMetadata: {
                ccLast4: paymentAccount.credit?.alias ?? '',
              },
            },
            skipCoolingPeriod: true,
          });
        }

        if (payment.isOrbital) {
          return commitOrder({
            ...commitInput,
            creditType: CartPaymentCardType.CREDIT,
            payment: {
              orbitalInput: {
                accountIdentifier,
              },
              fullName,
              ccMetadata: {
                ccLast4: paymentAccount.credit?.alias ?? '',
              },
            },
          });
        }

        if (payment.isFirstpay) {
          return commitOrder({
            ...commitInput,
            payment: {
              fullName,
              firstPayInput: {
                storedPaymentMethodId: accountIdentifier,
              },
              ccMetadata: {
                ccLast4: paymentAccount.credit?.alias ?? '',
              },
            },
            skipCoolingPeriod: true,
          });
        }

        if (payment.isPaycomet) {
          const payCometValues = paymentValues as IPaycometState;
          let fdThreeDSInput;
          if (
            (threeDSActiveFlow === ThreeDSType.METHOD ||
              threeDSActiveFlow === ThreeDSType.CHALLENGE) &&
            threeDSSaleId.current &&
            threeDSTransactionId
          ) {
            fdThreeDSInput = {
              threeDSDetails: {
                saleId: threeDSSaleId.current,
                threeDSOptions: {
                  transactionId: threeDSTransactionId,
                  challengeResponse: threeDSChallengeTokenResponse,
                },
              },
            };

            return commitOrder({
              ...commitInput,
              payment: {
                fullName: payCometValues.nameOnCard || '',
                paymentMethodBrand: paymentAccount?.paymentMethodBrand ?? undefined,
                paycometInput: {
                  storedPaymentMethodId: accountIdentifier,
                },
                ccMetadata: {
                  ccLast4: paymentAccount.credit?.alias ?? '',
                },
              },
              skipCoolingPeriod: true,
              threeDSDetails: fdThreeDSInput?.threeDSDetails ?? undefined,
            });
          }

          if (payCometValues.isPaypal) {
            return commitOrder({
              ...commitInput,
              creditType: CartPaymentCardType.PAYPAL,
              payment: {
                fullName: payCometValues.nameOnCard || '',
                ccMetadata: undefined,
                paycometInput: {
                  pspReference: '',
                  storePaymentMethod: payCometValues.saveCard || false,
                  storedPaymentMethodId: payCometValues.cardNumber || undefined,
                },
              },
              skipCoolingPeriod: true,
            });
          } else {
            return commitOrder({
              ...commitInput,
              payment: {
                fullName: payCometValues.nameOnCard || '',
                paymentMethodBrand: paymentAccount?.paymentMethodBrand ?? undefined,
                paycometInput: {
                  storedPaymentMethodId: accountIdentifier,
                },
                ccMetadata: {
                  ccLast4: paymentAccount.credit?.alias ?? '',
                },
              },
              skipCoolingPeriod: true,
            });
          }
        }

        // FD Normal Flow
        let threeDSInput;
        if (
          threeDSActiveFlow === ThreeDSType.METHOD ||
          threeDSActiveFlow === ThreeDSType.CHALLENGE
        ) {
          if (threeDSSaleId.current && threeDSTransactionId) {
            threeDSInput = {
              skipCoolingPeriod: true,
              threeDSDetails: {
                saleId: threeDSSaleId.current,
                threeDSOptions: {
                  transactionId: threeDSTransactionId,
                  challengeResponse: threeDSChallengeTokenResponse,
                },
              },
            };
          }
        }

        return commitOrder({
          ...commitInput,
          ...threeDSInput,
          payment: {
            firstDataInput: {
              accountIdentifier,
            },
            fullName,
            ccMetadata: {
              ccLast4: paymentAccount.credit?.alias ?? '',
            },
          },
        });
      }

      if (isCateringOrder) {
        return placeCateringOrder({
          cateringInfo: { pickupTime: cateringPickupDateTime },
          creditType: creditType as any,
          fdAccountId: accountIdentifier,
          storeEmail: email,
          rbiOrderId: serverOrder.rbiOrderId,
        }).catch(handleCommitError);
      }

      // This is a temporary hack. This will go away when we actually do a pseudo-injection here
      persistPaymentValuesToState({
        accountIdentifier,
        creditType: creditType!,
        fraudPreventionValues,
        googlePayDetails,
        applePayDetails,
        state: paymentValues,
      });

      // This is actually NEEDED to handle for dine-in and take-out orders
      return navigateToOrderConfirmation();
    },
    [
      selectedPaymentMethod,
      payment.paymentMethods,
      payment.prepaidReloadPaymentMethodId,
      payment.checkoutPaymentMethodId,
      payment.isAdyen,
      payment.isVrPayment,
      payment.isPayMark,
      payment.isOrbital,
      payment.isPaycomet,
      payment.isFirstpay,
      serverOrder,
      paymentValues,
      email,
      order,
      shouldCommitOrder,
      isCateringOrder,
      persistPaymentValuesToState,
      navigateToOrderConfirmation,
      reloadAmount,
      reloadGiftCardAndCheckout,
      billingCountry,
      store,
      enableQueryVrPaymentMerchantId,
      requestGooglePayPayment,
      getRestaurantVrPaymentMerchantId,
      prepaidRequestApplePayPayment,
      threeDSActiveFlow,
      commitOrder,
      threeDSTransactionId,
      threeDSChallengeTokenResponse,
      placeCateringOrder,
      cateringPickupDateTime,
      handleCommitError,
    ]
  );

  const {
    requestingPayment: isApplePayLoading,
    formatApplePayCartPayload,
    requestApplePayPayment,
  } = useApplePay({
    order,
    serverOrder,
    handleApplePay,
  });

  const placeOrderWithAccount = useCallback(async () => {
    const accountIdentifier = payment.paymentMethods.find(
      method =>
        method.accountIdentifier === payment.checkoutPaymentMethodId ||
        method.fdAccountId === payment.checkoutPaymentMethodId
    );
    if ((!accountIdentifier && !payment.isFreeOrderPayment) || !serverOrder) {
      return Promise.reject('Required parameters are not defined.');
    }

    const isRestaurantAvailableForOrdering = await isRestaurantAvailable(store);

    if (!isRestaurantAvailableForOrdering) {
      return openOrderingUnavailableDialog();
    }
    if (isApplePay(payment.checkoutPaymentMethodId)) {
      const applePayPayload = await formatApplePayCartPayload();
      return requestApplePayPayment(applePayPayload);
    }
    if (isGooglePay(payment.checkoutPaymentMethodId)) {
      const { countryCode, currencyCode } = getCountryAndCurrencyCodes(billingCountry);
      // Get the checkout gateway configs for google pay
      let merchantId = store.vrPaymentMerchantId ? store.vrPaymentMerchantId : '';

      if (enableQueryVrPaymentMerchantId && !merchantId) {
        merchantId = await getRestaurantVrPaymentMerchantId(store as IStore);
      }

      const { gateway, gatewayMerchantId } = getGooglePaymentsConfigs({
        transactionType: TransactionType.PAYMENT,
        countryCode,
        overrideMerchantId: merchantId,
        isDigitalWalletMigrationEnabled,
      });
      return requestGooglePayPayment({
        countryCode,
        currencyCode,
        total: serverOrder.cart.totalCents + (order?.tipAmount ?? 0),
        gatewayMerchantId,
        gateway,
      });
    }

    return processOrderWithAccount();
  }, [
    billingCountry,
    enableQueryVrPaymentMerchantId,
    formatApplePayCartPayload,
    getRestaurantVrPaymentMerchantId,
    isDigitalWalletMigrationEnabled,
    isRestaurantAvailable,
    openOrderingUnavailableDialog,
    order,
    payment.checkoutPaymentMethodId,
    payment.paymentMethods,
    processOrderWithAccount,
    requestApplePayPayment,
    requestGooglePayPayment,
    serverOrder,
    store,
  ]);

  const isOrderPriceLoading = !serverOrder || serverOrder.status === OrderStatus.PRICE_REQUESTED;

  const isOfferPaymentCardValid = useMemo(() => {
    if (!selectedOffer?.ruleSet) {
      return true;
    }

    const offerCardRule = findFirstRuleOfTypeThatApplies<IPaymentCardType>(
      RuleType.PaymentCardType,
      selectedOffer?.ruleSet
    );

    // paymentValues come from the form when adding a new payment
    // where selectedPaymentMethod is the payment method selected asssuming
    // not a new card is being added on checkout
    const cardType = paymentValues?.cardType || selectedPaymentMethod?.credit?.cardType;

    return offerCardRule ? offerCardRule.cardType === cardType : true;
  }, [selectedOffer, selectedPaymentMethod, paymentValues]);

  const cardIsValid =
    !!selectedPaymentMethod ||
    showAddPaymentMethod ||
    isAddGiftCardMethodSelected ||
    payment.paymentMethods.length === 0;

  const isValidTip = checkIfValidTip({
    tipAmount: order?.tipAmount,
    subTotalCents: serverOrder?.cart?.subTotalCents,
  });

  const isValidReloadPaymentMethodWithCard =
    !reloadAddPaymentMethod || (reloadAddPaymentMethod && reloadPaymentValues.saveCard);

  const offerIsValid = selectedOffer ? !offersCtx.offerValidationErrors.length : true;

  useEffect(alertOrderDeliveryMinimum, [alertOrderDeliveryMinimum]);
  useEffect(alertOrderCateringMinimum, [alertOrderCateringMinimum]);
  const { updateConnection } = useAppflow({ logger });

  const handleOrderCommit = useCallback(async () => {
    setCommittingOrder(true);
    isOrderStatusFailureHandledRef.current = false;
    try {
      if (!serverOrder) {
        throw new Error('Unable to get the rbiOrderId from serverOrder.');
      }

      if (isCurbside) {
        order.setCurbsidePickupOrderId(serverOrder.rbiOrderId);
        order.setCurbsidePickupOrderTimePlaced(new Date().toString());
      }

      const isPayPalRegistration =
        checkoutPaymentMethod?.accountIdentifier ===
          PAYPAL_PAYMENT_METHOD_PLACEHOLDER.accountIdentifier || isPaycometPaypalMethodSelected;

      const isPaymentWithSodexoVoucher =
        selectedPaymentMethod?.sodexo &&
        selectedPaymentMethod?.paymentMethodBrand === 'SODEXO_VOUCHER';

      const isPaymentWithChequeGourmetVoucher =
        selectedPaymentMethod?.chequeGourmet &&
        selectedPaymentMethod?.paymentMethodBrand === 'CHEQUE_GOURMET_VOUCHER';

      const isPaymentWithVoucher = isPaymentWithSodexoVoucher || isPaymentWithChequeGourmetVoucher;

      // Check if the order can be committed with an existing payment method
      if (
        (showAddPaymentMethod || reloadAddPaymentMethod || isPayPalRegistration) &&
        !(selectedPaymentMethod?.cash || isPaymentWithVoucher || payment.isFreeOrderPayment)
      ) {
        await placeOrder();
      } else {
        await placeOrderWithAccount();
      }
    } catch (error) {
      if (payment.isVrPayment) {
        logger.error({ error, message: 'VR Payment: commit failed' });
      }

      if (payment.isPayMark) {
        logger.error({ error, message: 'PayMark Payment: commit failed' });
      }

      if (payment.isFirstpay) {
        logger.error({ error, message: 'Firstpay Payment: commit failed' });
      }

      // Possible ThreeDS exception detected
      const graphQLErrorsData: ThreeDSDataPayment | null = handleThreeDSPayment(error);

      if (payment.isPaycomet && !graphQLErrorsData) {
        logger.error({ error, message: 'Paycomet Payment: commit failed' });
      }

      if (graphQLErrorsData) {
        const currentFlow = graphQLErrorsData?.type;
        const isThreeDSMethodCurrentFlow = currentFlow === ThreeDSType.METHOD;

        if (isThreeDSMethodCurrentFlow) {
          if (
            graphQLErrorsData.transactionId &&
            graphQLErrorsData.iframeContent &&
            graphQLErrorsData.saleId
          ) {
            setThreeDSActiveFlow(currentFlow);
            setThreeDSTransactionId(graphQLErrorsData.transactionId);
            setThreeDSIframeContent(graphQLErrorsData.iframeContent);
            threeDSSaleId.current = graphQLErrorsData.saleId;
          }
          return;
        }

        const isThreeDSChallengeCurrentFlow = currentFlow === ThreeDSType.CHALLENGE;
        if (isThreeDSChallengeCurrentFlow) {
          if (
            graphQLErrorsData.acsUrl &&
            graphQLErrorsData.challengeRequestToken &&
            graphQLErrorsData.transactionId &&
            graphQLErrorsData.saleId
          ) {
            setThreeDSActiveFlow(currentFlow);
            setThreeDSAcsUrl(graphQLErrorsData.acsUrl);
            setThreeDSChallengeRequest(graphQLErrorsData.challengeRequestToken);
            setThreeDSTransactionId(graphQLErrorsData.transactionId);
            threeDSSaleId.current = graphQLErrorsData.saleId;
          }
          return;
        }
      }

      cleanThreeDSFlow();
      handleCommitError(error);
    } finally {
      if (permissionToUpdate) {
        updateConnection(permissionToUpdate);
      }
    }
    return setCommittingOrder(false);
  }, [
    checkoutPaymentMethod,
    cleanThreeDSFlow,
    handleCommitError,
    isCurbside,
    isPaycometPaypalMethodSelected,
    logger,
    order,
    payment.isFirstpay,
    payment.isPayMark,
    payment.isPaycomet,
    payment.isVrPayment,
    permissionToUpdate,
    placeOrder,
    placeOrderWithAccount,
    reloadAddPaymentMethod,
    selectedPaymentMethod,
    serverOrder,
    setThreeDSAcsUrl,
    setThreeDSActiveFlow,
    setThreeDSChallengeRequest,
    setThreeDSIframeContent,
    setThreeDSTransactionId,
    showAddPaymentMethod,
    updateConnection,
  ]);
  useEffect(() => {
    if (isHandlingOrderCommit) {
      setIsHandlingOrderCommit(false);
      handleOrderCommit();
    }
  }, [handleOrderCommit, isHandlingOrderCommit]);

  const placeOrderAriaLabel = serviceMode
    ? formatMessage(
        { id: 'placeOrderDescription' },
        {
          serviceMode: getLocalizedServiceMode(serviceMode, formatMessage),
          price: formatCurrencyForLocale(serverOrder?.cart?.totalCents ?? 0),
        }
      )
    : isPlacingOrder
    ? formatMessage({ id: 'placingOrder' })
    : undefined;

  const showOrderProcessingDisclaimer =
    !isDelivery &&
    !isCateringOrder &&
    serviceMode !== ServiceMode.DRIVE_THRU &&
    serviceMode !== ServiceMode.TABLE_SERVICE &&
    !enableOrderTimedFire;

  const [ServiceModeUnavailableErrorDialog, openServiceModeUnavailableErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Service Mode Unavailable',
    onConfirm: () => navigate(routes.storeLocator),
  });

  return {
    cardIsValid,
    checkoutPaymentMethod,
    confirmStoreOnCheckout,
    dismissStoreConfirmationDialog,
    enableDelivery,
    enableImCloseDisplay,
    ErrorDialog,
    errors,
    focusElementWithError,
    handleAdyenInputChange,
    handleAdyenBrandDetected,
    handleAdyenInputErrors,
    handleVrPaymentSuccess,
    handlePayMarkSuccess,
    handlePaycometSuccess,
    handleFirstpaySuccess,
    setIsHandlingOrderCommit,
    handlePaymentMethodSelected,
    isApplePayLoading,
    isAdyenIdealMethodSelected,
    isCashPayment: checkoutPaymentMethod?.accountIdentifier === CASH_ACCOUNT_IDENTIFIER,
    isCateringOrder,
    isCommittingOrder,
    isGooglePayLoading,
    isOfferPaymentCardValid,
    isOrderPriceLoading,
    isPlacingOrder,
    isChallengeRequest,
    isThreeDSMethodFlow,
    isValidReloadPaymentMethodWithCard,
    isValidTip,
    needReloadAgain,
    offerIsValid,
    onChange,
    openServiceModeUnavailableErrorDialog,
    openStoreConfirmationModal,
    paymentMethods,
    paymentValues,
    placeOrderAriaLabel,
    reloadAmount,
    reloadPaymentValues,
    ServiceModeUnavailableErrorDialog,
    setCommitOrderAfterPricing,
    setIsAdyenIdealMethodSelected,
    setReloadPaymentValues,
    setShowAddPaymentMethod,
    setThreeDSChallengeTokenResponse,
    shouldCommitOrder,
    shouldConfirmStoreOnCheckout,
    showAddPaymentMethod,
    showOrderProcessingDisclaimer,
    showPaymentMethodSelection,
    showPrepaidTransferBalance,
    showReloadPaymentMethods,
    StoreConfirmationDialog,
    threeDS: {
      iframeContent: threeDSIframeContent,
      transactionId: threeDSTransactionId,
      activeFlow: threeDSActiveFlow,
      acsUrl: threeDSAcsUrl,
      challengeRequest: threeDSChallengeRequest,
      isChallengeRequest,
      isThreeDSMethodFlow,
    },
    threeDSChallengeTokenResponse,
    validateCreditCardForm,
    isAddGiftCardMethodSelected,
    setIsAddGiftCardMethodSelected,
    setIsPaycometPaypalMethodSelected,
    isPaycometPaypalMethodSelected,
    setIsPaycometSodexoMethodSelected,
    isPaycometSodexoMethodSelected,
    setIsPaycometChequeGourmetMethodSelected,
    isPaycometChequeGourmetMethodSelected,
    setIsFreeOrder,
    isFreeOrder,
  };
};
